/**
 * CaptchaStats
 * ────────────
 * Admin analytics card for captcha verification attempts. Renders next to
 * the CaptchaSettingsEditor in /admin/settings → Captcha tab.
 *
 * Shows for the selected window (7 / 30 / 90 days):
 *   • 4 KPI cards: Success / Fail / Bypassed / Success rate
 *   • Stacked bar chart with daily counts
 *   • Top error codes from Cloudflare (helps diagnose attack patterns)
 *
 * Every successful and failed siteverify call is logged by
 * `utils/turnstile.py:_log_attempt` into the `captcha_attempts` Mongo
 * collection. This component just reads `/api/config/captcha-stats`.
 */
import React, { useEffect, useState } from 'react';
import { BarChart3, ShieldCheck, ShieldX, ShieldOff, Percent, Loader2, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WINDOWS = [
  { value: 7,  label: '7 days'  },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

export default function CaptchaStats({ token }) {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async (d = windowDays) => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/config/captcha-stats?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json?.detail || `HTTP ${r.status}`);
      setData(json);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(windowDays); }, [windowDays]);   // reload when window changes

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mt-4" data-testid="captcha-stats">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-600" />
          Captcha Analytics
        </h2>
        <div className="flex items-center gap-1.5">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setWindowDays(w.value)}
              className={`px-2.5 py-1 rounded-md text-xs font-bold border transition ${
                windowDays === w.value
                  ? 'bg-cyan-600 text-white border-cyan-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`captcha-stats-range-${w.value}`}
            >
              {w.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => load(windowDays)}
            disabled={loading}
            className="ml-1 p-1.5 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            data-testid="captcha-stats-refresh"
            title="Reload"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {err && (
        <div className="mb-3 px-3 py-2 rounded bg-rose-50 border border-rose-200 text-xs text-rose-800">
          Failed to load: {err}
        </div>
      )}

      {loading && !data && (
        <div className="py-6 text-center text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
        </div>
      )}

      {data && (
        <>
          {/* ─── KPI cards ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
            <Kpi
              icon={ShieldCheck}
              tone="emerald"
              label="Verified"
              value={data.totals.success}
              hint="Cloudflare accepted the token"
              testid="kpi-success"
            />
            <Kpi
              icon={ShieldX}
              tone="rose"
              label="Failed"
              value={data.totals.fail}
              hint="Bot / expired / network errors"
              testid="kpi-fail"
            />
            <Kpi
              icon={ShieldOff}
              tone="amber"
              label="Bypassed"
              value={data.totals.bypassed}
              hint="Captcha disabled or secret missing"
              testid="kpi-bypassed"
            />
            <Kpi
              icon={Percent}
              tone="cyan"
              label="Success rate"
              value={data.totals.success_rate === null ? '—' : `${data.totals.success_rate}%`}
              hint="success / (success + fail)"
              testid="kpi-success-rate"
            />
          </div>

          {/* ─── Daily chart ──────────────────────────────────────── */}
          {data.totals.total === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-lg" data-testid="captcha-stats-empty">
              No captcha attempts recorded in the last {data.window_days} day{data.window_days === 1 ? '' : 's'} yet.
              <br />
              <span className="text-[11px] text-slate-400">Once visitors hit your registration form, attempts will show up here.</span>
            </div>
          ) : (
            <div className="h-56 w-full" data-testid="captcha-stats-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_day} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="date"
                    fontSize={10}
                    tickFormatter={(d) => d.slice(5)}   /* "MM-DD" */
                    interval="preserveStartEnd"
                  />
                  <YAxis fontSize={10} allowDecimals={false} width={28} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #cbd5e1' }}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  <Bar dataKey="success"  stackId="x" fill="#10b981" name="Verified"  radius={[0,0,0,0]} />
                  <Bar dataKey="fail"     stackId="x" fill="#f43f5e" name="Failed"    radius={[0,0,0,0]} />
                  <Bar dataKey="bypassed" stackId="x" fill="#f59e0b" name="Bypassed"  radius={[2,2,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ─── Top error codes ──────────────────────────────────── */}
          {(data.top_error_codes?.length || 0) > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-200" data-testid="captcha-stats-codes">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                Top Cloudflare error codes
              </h3>
              <ul className="flex flex-wrap gap-1.5 text-[11px]">
                {data.top_error_codes.map((c) => (
                  <li
                    key={c.code}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-mono"
                    title={errorCodeHint(c.code)}
                  >
                    <span>{c.code}</span>
                    <b className="text-slate-900">×{c.count}</b>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, tone, label, value, hint, testid }) {
  const palettes = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    rose:    'bg-rose-50    border-rose-200    text-rose-900',
    amber:   'bg-amber-50   border-amber-200   text-amber-900',
    cyan:    'bg-cyan-50    border-cyan-200    text-cyan-900',
  };
  const iconTone = {
    emerald: 'text-emerald-600',
    rose:    'text-rose-600',
    amber:   'text-amber-600',
    cyan:    'text-cyan-600',
  };
  return (
    <div className={`rounded-lg border p-3 ${palettes[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon className={`w-3.5 h-3.5 ${iconTone[tone]}`} /> {label}
      </div>
      <div className="text-2xl font-extrabold tabular-nums mt-0.5 leading-tight">{value}</div>
      <div className="text-[10px] opacity-60 mt-0.5 leading-tight">{hint}</div>
    </div>
  );
}

function errorCodeHint(code) {
  // Common Cloudflare Turnstile error codes — surfaced as tooltip text.
  const m = {
    'invalid-input-response': 'Token from browser was invalid (typical for bots).',
    'missing-input-response': 'No token sent — form bypassed widget.',
    'timeout-or-duplicate':   'Token expired or reused.',
    'invalid-input-secret':   'Secret key rejected — your config is wrong.',
    'missing-input-secret':   'Backend didn\'t send a secret.',
    'bad-request':            'Cloudflare rejected the request format.',
    'internal-error':         'Cloudflare hiccup — retry later.',
  };
  return m[code] || 'Cloudflare Turnstile error code.';
}
